import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useClickAway } from 'react-use';
import { useSubmit, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

// Styled Components
import {
  ContainerOT,
  ButtonContainer,
  Container,
  InfoMessage,
  ActionMenuContainer,
  ActionMenuList,
} from './styles';

// Components
import { WorkOrder } from './WorkOrder';
import { SubWorkOrder } from './SubWorkOrder';
import { ActionOT } from './ActionOT';
import { RouteSegmentOT } from './RouteSegmentOT';
import { MainButton } from '../../inputs/buttons/MainButton';

// Types
import { ShowOTTypes } from './types';
import { SpecialTag } from '../SpecialTag';
import { variant } from '../SpecialTag/types';
import { Order, OrderState, OrderType } from '../../../types/orders';
import { Role } from '../../../types/auth';

// Utils
import { createFormDataFromValues } from '../../../utils/forms';
import { discardPreviousPartialAction } from '../../../utils/discardPreviousPartialAction';
import { releaseCommunicationCreationSelection } from '../../../utils/communicationCreationDraft';
import {
  peekChildCardAnchor,
  clearChildCardAnchor,
} from '../../../utils/childCardAnchor';
import {
  removeNewAccidentStorage,
  removeNewActionStorage,
} from '../../../utils/resetData';

import {
  canCreateContentInState,
  TABLET_CONTENT_CREATION_STATES,
  BLOCKED_STATES_FOR_CONTENT,
} from '../../../utils/rolePermissions';
import {
  canSeeCreateActionButtonInRoute,
  canSegmentTakeLinks,
} from '../../../utils/surveillanceRoute';

// Redux
import { roidOTSelected } from '../../../state/actions/ui/roidOTSelectedGeneralView';
import { actionTabsUnlockAll } from '../../../state/actions/ui/actionTabs';
import {
  selectedRowModalTable,
  selectedRowModalAcceptData,
} from '../../../state/actions/ui/selectRowTableModal';
import { openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

// Constants
import { formActionNaming } from '../../forms/ActionForms/constants';
import { formNaming } from '../../forms/NewCommunicationForm/constants';
import { groupLanes, groupTracks } from '../../../data/orders';

// Hooks
import { useAuth } from '../../../hooks/useAuth';
import { useIsPortraitTablet } from '../../../hooks/useMediaBreakPoints';
import { useCloseOrderConfirmation } from '../../../hooks/useCloseOrderConfirmation';

// GQL
import { client } from '../../../gql/client';
import {
  GET_CAPITOL_BY_ID,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../gql/queries/orders/predefinedData';
import { AccidentOT } from './AccidentOT';
import { CommunicationOT } from './CommunicationOT';

// Respiro por encima de la tarjeta a la que se vuelve, para que no quede
// pegada al borde superior de la ventana.
const ANCHOR_TOP_MARGIN_PX = 16;

export const ShowOT = ({
  data,
  revalidatorFunction,
  multimediaPath = './multimedia',
  onActionRelateToSegment,
  onActionUnlinkFromSegment,
  onSegmentNavigateToLinkedAction,
  onSegmentNavigateToLinkedExternal,
  onSegmentNavigateToLinkedWork,
  onSegmentLinkExternalOrder,
  onSegmentChangeExternalOrder,
  onSegmentUnlinkExternalOrder,
  onSegmentEdit,
  canAttachPhotos,
  canCreateBasicAction,
  canChangeSegmentCause,
  restoreChildCardAnchor,
}: ShowOTTypes): JSX.Element | null => {
  const { t } = useTranslation(['workOrders', 'common']);
  const submit = useSubmit();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentRole } = useAuth();
  const isPortraitTablet = useIsPortraitTablet();
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  // Vuelta a la tarjeta hija de la que se salió. La LECTURA es pura y se hace
  // en el render porque la tarjeta tiene que nacer ya desplegada; el BORRADO
  // vive en el efecto de abajo. Separarlos no es un capricho: bajo
  // `React.StrictMode` este inicializador se invoca DOS veces, así que si leer
  // borrase, la segunda invocación encontraría el almacén vacío y ganaría el
  // `null` — la tarjeta volvía cerrada y sin desplazar (cazado en vivo).
  const anchorParentOrderId = data?.[0]?.id;
  const [anchoredRoid] = useState<string | null>(() =>
    restoreChildCardAnchor ? peekChildCardAnchor(anchorParentOrderId) : null
  );

  useLayoutEffect(() => {
    // Un solo uso: se borra tanto si ha servido como si no. Si la tarjeta ya
    // no está (la hija se anuló, o el ancla era de otra OT), dejarla puesta
    // solo serviría para mover la pantalla en una visita futura que nadie ha
    // pedido.
    //
    // 🔴 Pero SOLO si había un id de OT con el que compararla. Sin él no se ha
    // llegado a mirar si el ancla era de esta pantalla, así que borrarla sería
    // tirar el trabajo de otro: `ShowOT` ejecuta sus hooks ANTES del guard que
    // devuelve `null` con `data` vacío.
    if (restoreChildCardAnchor && anchorParentOrderId) clearChildCardAnchor();
    if (!anchoredRoid) return;
    const nodes = cardsContainerRef.current?.querySelectorAll<HTMLElement>(
      '[data-child-roid]'
    );
    // Se compara el `dataset` en vez de meter el ROID dentro de un selector de
    // atributo: así no hay nada que escapar y no se depende de `CSS.escape`.
    const target = Array.from(nodes ?? []).find(
      (node) => node.dataset.childRoid === anchoredRoid
    );
    if (!target) return;
    // Posición calculada a mano en vez de `scrollIntoView`, para dejar un
    // respiro arriba. La tarjeta ya está desplegada en este mismo render (el
    // desplegado es el valor INICIAL de su estado), así que la medida es buena.
    window.scrollTo({
      top:
        target.getBoundingClientRect().top +
        window.scrollY -
        ANCHOR_TOP_MARGIN_PX,
      behavior: 'auto',
    });
  }, [anchoredRoid, restoreChildCardAnchor, anchorParentOrderId]);

  // SR context: hide accident/communication buttons (only actuaciones in SR)
  const isSurveillanceRouteContext = location.pathname.startsWith(
    '/surveillance-route/'
  );

  // Propiedad de la patrulla (2026-09-04): en el RV, crear una básica y
  // vincular a un tramo lo decide la página con `canCreateBasicActionInRoute`
  // (equipo + dueño del cronómetro para la tablet; gestión siempre) y aquí
  // solo se obedece. Fuera del RV la prop no llega y nada cambia.
  //
  // 🔴 Se DESHABILITA el botón, no se esconde: es el mismo patrón que ya usa
  // el gating por rol × estado (`canCreateAction`), y en tablet vertical
  // «Crear Actuación» vive dentro del menú de «Crear +». Escondiéndolo, el
  // menú se abría vacío y el operario pulsaba sin que pasara nada — cazado en
  // tablet el 2026-09-04. Las dos ramas del render, vertical y escritorio,
  // aplican la MISMA condición: la primera versión solo tocó la de escritorio
  // y en la tablet el botón seguía activo.
  const routeAllowsBasicAction =
    !isSurveillanceRouteContext || canCreateBasicAction === true;

  // Qué botones de crear se PINTAN (2026-10-09). Se decide una sola vez y lo
  // obedecen las dos ramas del render, vertical y escritorio: la regresión
  // del 2026-09-04 vino justo de tocar solo una.
  //
  // En el RV «Crear Actuación» es SOLO de gestión
  // (`canSeeCreateActionButtonInRoute`, decisión del usuario): la patrulla
  // crea la básica por el cronómetro o por la pastilla «Vincular a actuación»
  // del tramo, que siguen con `canCreateBasicAction`. Esconderlo no contradice
  // el «se deshabilita, no se esconde» de arriba: aquello es para quien TIENE
  // el botón y en este recorrido no puede usarlo; la tablet ya no lo tiene en
  // ninguno.
  const showCreateAccident =
    currentRole !== Role.RoomTeam && !isSurveillanceRouteContext;
  const showCreateAction =
    currentRole !== Role.RoomTeam &&
    (!isSurveillanceRouteContext ||
      canSeeCreateActionButtonInRoute(currentRole));
  const showCreateCommunication =
    currentRole !== Role.Tablet && !isSurveillanceRouteContext;
  // 🔴 Sin nada que crear, el menú «Crear +» de la tablet vertical no se
  // pinta: abierto vacío, se pulsa y no pasa nada (2026-09-04). En el RV le
  // pasaba ya a sala, que ahí no crea nada.
  const hasCreateButtons =
    showCreateAccident || showCreateAction || showCreateCommunication;

  useClickAway(actionMenuRef, () => setIsActionMenuOpen(false));

  // T99: Modal de cerrar-OT. Escucha las notas pendientes (Redux) y lo abre
  // con la info de la OT resuelta (roid/tipo/descripción). Factorizado en hook
  // compartido (ShowOT / ActionsValidation / Gestión Horaria); el hook invalida
  // informe + listas al cerrar.
  //
  // El detalle solo enseña el aviso de SU orden (campaña, OT o recorrido): una
  // nota de otra orden espera a que llegues a la suya o a una pantalla general.
  // Spec 2026-10-12-aviso-cierre-recorrido § 12.
  const containerOrderId = (
    data.find((order) => order.type === 'campaign') ??
    data.find(
      (order) =>
        order.type === 'generic' || order.type === OrderType.SurveillanceRoute
    )
  )?.id;
  useCloseOrderConfirmation({ kind: 'order', orderId: containerOrderId });

  // El padre no-campaña puede ser una OT genérica o la Order gemela de un RV
  // (desde 2026-07-21 el selector ya no enmascara el RV como `generic`).
  const orderGeneric = data.find(
    (order) =>
      order.type === 'generic' || order.type === OrderType.SurveillanceRoute
  );
  const campaignOrder = data.find((order) => order.type === 'campaign');

  // Get the parent order and its state
  const parentOrder = orderGeneric || campaignOrder;
  const orderState = (parentOrder?.tag || parentOrder?.state) as OrderState;

  // Legacy isDisabled for backwards compatibility (used elsewhere in component)
  const [isDisabled, setIsDisabled] = useState(
    orderGeneric?.tag !== variant.Open || campaignOrder?.tag !== variant.Open
  );

  useEffect(() => {
    orderGeneric
      ? setIsDisabled(orderGeneric?.tag !== variant.Open)
      : setIsDisabled(campaignOrder?.tag !== variant.Open);
  }, [data]);

  // Defensive guard: prevent crash if data is empty/invalid (e.g. offline navigation)
  if (!data || data.length === 0 || !data[0]) {
    return null;
  }

  // NEW: Role-aware content creation permissions
  const canCreateAction = canCreateContentInState(
    currentRole as Role,
    orderState,
    'action'
  );
  const canCreateAccident = canCreateContentInState(
    currentRole as Role,
    orderState,
    'accident'
  );
  const canCreateCommunication = canCreateContentInState(
    currentRole as Role,
    orderState,
    'communication'
  );

  // Check if order is in a final blocked state (for all roles)
  const isOrderBlocked = BLOCKED_STATES_FOR_CONTENT.includes(orderState);

  // Aviso al Tablet: necesita un estado de trabajo para crear/editar
  // contenido. La MISMA constante que decide canCreateAction — antes comparaba
  // solo contra WorkInProgress y en el RV in-progress pintaba el banner con el
  // botón HABILITADO (contradicción cazada 2026-07-21).
  // En el RV NO se muestra: su máquina de estados es distinta (el estado del
  // padre lo derivan los tramos) y "cambia a Trabajo en curso" no es un gesto
  // que exista — el operario arranca/finaliza TRAMOS desde el cronómetro. En
  // `open` la guía es "Iniciar recorrido" (cabecera); en `paused`/`in-progress`
  // el botón ya está habilitado. El botón deshabilitado basta como señal.
  const tabletNeedsStateChange =
    currentRole === Role.Tablet &&
    !isSurveillanceRouteContext &&
    !TABLET_CONTENT_CREATION_STATES.includes(orderState) &&
    !isOrderBlocked;
  const handleCreate = (type: string) => async () => {
    const dataToMakeRelation = {
      id: data[0].id,
      modelTypeId: data[0].modelTypeId,
      roid: data[0].roid,
    };

    // BUG #19 FIX: Resolve Capitol/Subcapitol IDs to names via GraphQL
    let capitolName = '';
    let subCapitolName = '';

    try {
      const [capitolResult, subCapitolResult] = await Promise.all([
        data[0].capitol
          ? client.query({
              query: GET_CAPITOL_BY_ID,
              variables: { id: data[0].capitol },
            })
          : Promise.resolve(null),
        data[0].subCapitol
          ? client.query({
              query: GET_SUB_CAPITOL_BY_ID,
              variables: { id: data[0].subCapitol },
            })
          : Promise.resolve(null),
      ]);

      capitolName = capitolResult?.data?.capitol?.name || '';
      subCapitolName = subCapitolResult?.data?.subCapitol?.name || '';
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error resolving capitol/subcapitol names:', error);
    }

    dispatch(
      roidOTSelected(
        OrderType[
          (type.charAt(0).toUpperCase() +
            type.slice(1)) as keyof typeof OrderType
        ],
        Array.isArray(data[0].roid) ? data[0].roid.join('.') : data[0].roid,
        data[0].id,
        capitolName,
        subCapitolName
      )
    );

    if (type === OrderType.Accident) {
      removeNewAccidentStorage();
    } else if (type === OrderType.Communication) {
      // Remove any previous communication storage if needed
      sessionStorage.removeItem(formNaming.NEW_COMMUNICATION_FORM);
      // Alta NUEVA: fuera también la OT/campaña que dejó elegida el alta
      // anterior en Redux (persistida en `localStorage`). Sin esto, la clave
      // que NO se escribe abajo (p. ej. la de campaña al entrar desde una OT)
      // seguía con el padre de otra alta (K3, QA 2026-11-03).
      releaseCommunicationCreationSelection(dispatch);

      // Determine correct Redux key based on parent order type (OT vs Campaign)
      const isCampaignParent = data[0].type === OrderType.Campaign;
      const communicationFormKey = isCampaignParent
        ? formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN
        : formNaming.NEW_COMMUNICATION_FORM_OT;

      // BUG #19 FIX: Dispatch Redux action para pre-seleccionar la OT/Campaña padre
      dispatch(
        selectedRowModalTable(communicationFormKey, {
          roid: Array.isArray(data[0].roid)
            ? data[0].roid.join('.')
            : data[0].roid,
          id: data[0].id,
          capitol: capitolName, // Display name for SelectionOT
          subCapitol: subCapitolName, // Display name for SelectionOT
          capitolId: data[0].capitol, // T36: ID for form pre-fill
          subCapitolId: data[0].subCapitol, // T36: ID for form pre-fill
          classification: data[0].classificationId, // T36: Classification ID to auto-fill
          predefinedOrder: data[0].predefine, // T36: Predefinido ID to auto-fill
          direction: data[0].direction, // T39: Direction to auto-fill
          teamRelationships: data[0].teamRelationships, // Tarea #8: Team ID to auto-fill
        })
      );
      dispatch(selectedRowModalAcceptData(communicationFormKey));
    } else if (type === OrderType.Action) {
      // Si el asistente anterior de ESTA pestaña quedó a medias (parcial), se
      // descarta — mismo helper que usan el cronómetro y la pill del tramo.
      await discardPreviousPartialAction();
      removeNewActionStorage();
    }

    // INTEGRACIÓN 9: Desbloquear TODAS las tabs para navegación libre
    dispatch(actionTabsUnlockAll());

    const formData = createFormDataFromValues({
      ...dataToMakeRelation,
      // P9 auto-create (2026-07-21): en el RV, Crear Actuació no pasa por el
      // form de Descripción — el router action crea la básica con datos
      // heredados y aterriza en el wizard reducido (details/location).
      // Offline o error, el action degrada solo a pass-data (form).
      intent:
        isSurveillanceRouteContext && type === 'action'
          ? 'create-basic-action'
          : 'pass-data',
    });

    // Fix ronda 4 (FR-8.5/FR-9.1): en el SR este componente se monta bajo la
    // ruta hija `child-orders`, así que el action relativo `new-action`
    // resolvía a /surveillance-route/:id/child-orders/new-action (no existe →
    // 404). Las rutas new-* del SR son hermanas de child-orders → subir un
    // nivel. En WO/Campaign el relativo directo sigue siendo el correcto.
    submit(formData, {
      method: 'POST',
      action: isSurveillanceRouteContext ? `../new-${type}` : `new-${type}`,
    });
  };

  // Check if user is Tablet and order is in PendingWork state (legacy check)
  const isPendingWorkForTablet =
    currentRole === Role.Tablet &&
    (orderGeneric?.tag === variant.PendingWork ||
      campaignOrder?.tag === variant.PendingWork);

  const childIdsWithPhotos = data
    .filter(
      (order) =>
        (order.type === OrderType.Action ||
          order.type === OrderType.Accident) &&
        order.imageIDs &&
        order.imageIDs.length > 0
    )
    .map((order) => order.id);

  return (
    <Container ref={cardsContainerRef}>
      {/* Message for Tablet when order is not in WorkInProgress */}
      {tabletNeedsStateChange && (
        <InfoMessage>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          {t('tabletOpenStateMessage')}
        </InfoMessage>
      )}

      {/* Message when order is in final state (End/Annulled) */}
      {isOrderBlocked && (
        <InfoMessage>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          {t(
            orderState === OrderState.End
              ? 'orderClosedMessage'
              : 'orderAnnulledMessage'
          )}
        </InfoMessage>
      )}

      {/* Legacy message for Tablet in PendingWork */}
      {isPendingWorkForTablet && isDisabled && !tabletNeedsStateChange && (
        <InfoMessage>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          {t('pendingWorkInfoMessage')}
        </InfoMessage>
      )}

      <ButtonContainer data-testid="showot-buttons">
        {isPortraitTablet ? (
          hasCreateButtons && (
            <ActionMenuContainer ref={actionMenuRef}>
              <MainButton
                text={t('common:buttons.createPlus', 'Crear +')}
                variant="secondary"
                onClick={() => setIsActionMenuOpen(!isActionMenuOpen)}
              />
              {isActionMenuOpen && (
                <ActionMenuList>
                  {showCreateAccident && (
                    <MainButton
                      text={t('createAccident')}
                      variant="secondary"
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        handleCreate(OrderType.Accident)();
                      }}
                      disabled={!canCreateAccident}
                    />
                  )}
                  {/* En el RV, solo gestión (Coex/Capataz/SuperAdmin) —
                    ver `showCreateAction`. */}
                  {showCreateAction && (
                    <MainButton
                      text={t('createAction')}
                      variant="primary"
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        handleCreate(OrderType.Action)();
                      }}
                      disabled={!canCreateAction || !routeAllowsBasicAction}
                    />
                  )}
                  {showCreateCommunication && (
                    <MainButton
                      text={t('createCommunication')}
                      variant="tertiary"
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        handleCreate(OrderType.Communication)();
                      }}
                      disabled={!canCreateCommunication}
                    />
                  )}
                </ActionMenuList>
              )}
            </ActionMenuContainer>
          )
        ) : (
          <>
            {/* Create Accident button - hidden in SR context */}
            {showCreateAccident && (
              <MainButton
                text={t('createAccident')}
                variant="secondary"
                onClick={handleCreate(OrderType.Accident)}
                disabled={!canCreateAccident}
              />
            )}
            {/* Create Action button — en el RV, solo gestión
                (Coex/Capataz/SuperAdmin) — ver `showCreateAction`. */}
            {showCreateAction && (
              <MainButton
                text={t('createAction')}
                variant="primary"
                onClick={handleCreate(OrderType.Action)}
                disabled={!canCreateAction || !routeAllowsBasicAction}
              />
            )}
            {/* Create Communication button - hidden in SR context */}
            {showCreateCommunication && (
              <MainButton
                text={t('createCommunication')}
                variant="tertiary"
                onClick={handleCreate(OrderType.Communication)}
                disabled={!canCreateCommunication}
              />
            )}
          </>
        )}
        {childIdsWithPhotos.length > 0 && (
          <MainButton
            text={t('pages:workOrders.downloadPhotos', 'Descargar fotos')}
            variant="quaternary"
            onClick={() =>
              dispatch(
                openModal(modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS, {
                  selectedRows: childIdsWithPhotos,
                })
              )
            }
          />
        )}
        {childIdsWithPhotos.length > 0 && (
          <MainButton
            text={t('pages:workOrders.viewPhotos', 'Ver fotos')}
            variant="quaternary"
            onClick={() => navigate(multimediaPath)}
          />
        )}
        {data[0].type === OrderType.Campaign && (
          <SpecialTag variant={variant.Campaign} title={data[0].name} />
        )}
      </ButtonContainer>
      <ContainerOT>
        {data?.map((order, index) => {
          if (
            order.type === OrderType.Generic ||
            order.type === OrderType.Campaign ||
            order.type === OrderType.SurveillanceRoute
          ) {
            return (
              <WorkOrder
                key={index}
                {...order}
                groupTracks={groupTracks}
                groupLanes={groupLanes}
                name={
                  order.type === OrderType.Campaign
                    ? t('tables:headers.campaign')
                    : t('workOrder')
                }
                revalidatorFunction={revalidatorFunction}
              />
            );
          } else if (order.type === OrderType.SubOrder) {
            return (
              <SubWorkOrder
                key={index}
                {...order}
                revalidatorFunction={revalidatorFunction}
              />
            );
          } else if (order.type === OrderType.Action) {
            return (
              <ActionOT
                parentOrder={{
                  id: data[0].id,
                  type: data[0].type,
                  roid: Array.isArray(data[0].roid)
                    ? data[0].roid.join('.')
                    : data[0].roid,
                }}
                key={index}
                {...order}
                initiallyExpanded={
                  anchoredRoid !== null && order.roid === anchoredRoid
                }
                revalidatorFunction={revalidatorFunction}
                state={order?.state}
                stateParentExpeditionOrder={orderGeneric?.state}
                // P15 Fase 1 (2026-07-08): solo `RouteChildOrders` (contexto RV)
                // aporta este factory — WorkOrderDetailed/CampaignDetailed no lo
                // pasan, así que aquí queda `undefined` y la pill no renderiza
                // (comportamiento sin cambios para esos dos consumidores).
                onRelateClick={
                  isSurveillanceRouteContext && onActionRelateToSegment
                    ? () => onActionRelateToSegment(order)
                    : undefined
                }
                onUnlinkClick={
                  isSurveillanceRouteContext &&
                  onActionUnlinkFromSegment &&
                  order.linkedSegmentEntityId
                    ? () => onActionUnlinkFromSegment(order)
                    : undefined
                }
              />
            );
          } else if (order.type === OrderType.Accident) {
            return (
              <AccidentOT
                key={index}
                {...order}
                accidentID={order.accidentID || order.id} // Fallback to order.id if accidentID not available
                parentOrder={{
                  id: data[0].id,
                  type: data[0].type,
                  roid: Array.isArray(data[0].roid)
                    ? data[0].roid.join('.')
                    : data[0].roid,
                }}
                initiallyExpanded={
                  anchoredRoid !== null && order.roid === anchoredRoid
                }
                revalidatorFunction={revalidatorFunction}
                state={order?.state}
                stateParentExpeditionOrder={orderGeneric?.state}
              />
            );
          } else if (order.type === OrderType.Communication) {
            return (
              <CommunicationOT
                key={index}
                {...order}
                communicationID={order.communicationID || order.id} // Fallback to order.id if communicationID not available
                parentOrder={{
                  id: data[0].id,
                  type: data[0].type,
                  roid: Array.isArray(data[0].roid)
                    ? data[0].roid.join('.')
                    : data[0].roid,
                }}
                initiallyExpanded={
                  anchoredRoid !== null && order.roid === anchoredRoid
                }
                revalidatorFunction={revalidatorFunction}
                state={order?.state}
                stateParentExpeditionOrder={orderGeneric?.state}
              />
            );
          } else if (order.type === OrderType.RouteSegment) {
            // V4 P8 FR-8.3 (2026-04-22): cards de Tramos renderizadas bajo el RV padre.
            // Los cross-links (basic action / external) se muestran via pills D2.
            //
            // P15 Fase 1 (2026-07-08): `onRelateClick`/`onLinkedExternalClick`
            // navegan (no abren modal). Se construyen truthy SOLO cuando la
            // fila ya está vinculada en la dirección correspondiente; y el
            // click de "Pendent d'Avís a sala" (que también invoca
            // `onRelateClick`) queda no-op al no haber vínculo aún.
            //
            // 2026-08-03 — el hueco que dejaba eso: con `onRelateClick`
            // truthy SOLO si ya hay vínculo, la pill "Vincular a actuació"
            // del tramo (que exige `!linkedOrderRoid`) no podía aparecer
            // JAMÁS, y un tramo con causa Actuación sin básica se quedaba sin
            // salida en la interfaz. Se cubre con `canCreateBasicAction`: la
            // pill lanza la cadena P9 (crear básica + wizard + `?fromSegment`),
            // que es exactamente lo que hace el cierre online. El permiso es
            // el MISMO que HABILITA el botón "Crear Actuación" del RV
            // (`canCreateAction && routeAllowsBasicAction`), pero NO el que lo
            // PINTA: desde 2026-10-09 el botón es solo de gestión y la pill
            // sigue siendo también de la patrulla — es su salida cuando el
            // cierre con causa Actuación se quedó sin básica.
            const isLinkedAction =
              order.pauseCause === 'action' && Boolean(order.linkedOrderRoid);
            const isLinkedExternal =
              order.pauseCause === 'external-call' &&
              Boolean(order.linkedOrderRoid);
            // Un tramo ANULADO no admite vínculos nuevos ni cambios (QA
            // T3.1+a): sin «Vincular aviso», «Editar · Desvincular» ni
            // «Vincular a actuación». «Ver» lo vinculado, sí. Como la tabla.
            const takesLinks = canSegmentTakeLinks({
              state: order.tag || order.state,
            });
            return (
              <RouteSegmentOT
                key={index}
                roid={
                  Array.isArray(order.roid)
                    ? order.roid.join('.')
                    : (order.roid as string)
                }
                tag={order.tag || order.state}
                date={order.date || order.startDate || 0}
                endDate={order.endDate}
                pkInit={order.pkInit}
                pkEnd={order.pkEnd}
                roadStretches={order.roadStretches}
                isTransferred={order.isTransferred}
                transferredFrom={order.transferredFrom}
                direction={order.direction}
                lane={order.lane}
                description={order.description}
                pauseCause={order.pauseCause}
                pauseCauseDetail={order.pauseCauseDetail}
                pendingExternalLink={order.pendingExternalLink}
                pendingActionLink={order.pendingActionLink}
                linkedOrderRoid={order.linkedOrderRoid}
                linkedExternalWorks={
                  isLinkedExternal ? order.linkedExternalWorks : undefined
                }
                onLinkedWorkClick={
                  onSegmentNavigateToLinkedWork
                    ? (work) => onSegmentNavigateToLinkedWork(work, order)
                    : undefined
                }
                onRelateClick={
                  isLinkedAction && onSegmentNavigateToLinkedAction
                    ? () => onSegmentNavigateToLinkedAction(order)
                    : undefined
                }
                onLinkedExternalClick={
                  isLinkedExternal && onSegmentNavigateToLinkedExternal
                    ? () => onSegmentNavigateToLinkedExternal(order)
                    : undefined
                }
                // Aviso a sala desde la card (2026-09-24).
                onLinkExternalClick={
                  takesLinks &&
                  order.pauseCause === 'external-call' &&
                  !order.linkedOrderRoid &&
                  onSegmentLinkExternalOrder
                    ? () => onSegmentLinkExternalOrder(order)
                    : undefined
                }
                onEditExternalClick={
                  takesLinks && isLinkedExternal && onSegmentChangeExternalOrder
                    ? () => onSegmentChangeExternalOrder(order)
                    : undefined
                }
                onUnlinkExternalClick={
                  takesLinks && isLinkedExternal && onSegmentUnlinkExternalOrder
                    ? () => onSegmentUnlinkExternalOrder(order)
                    : undefined
                }
                // Un tramo anulado no se corrige (remate del QA 15-oct): ni
                // aquí ni en la tabla de Tramos.
                onEditClick={
                  onSegmentEdit && order.state !== OrderState.Annulled
                    ? () => onSegmentEdit(order)
                    : undefined
                }
                canCreateBasicAction={
                  takesLinks && canCreateAction && routeAllowsBasicAction
                }
                // D6: id de la ENTITY RouteSegment (no el de la Order) — es el
                // que esperan updateSegmentPauseCause y ?fromSegment.
                id={order.segmentId}
                // Fotos del tramo (2026-08-09): `orderId` es la Order gemela,
                // que es a la que cuelgan las imágenes; `imageIDs` se resuelve
                // al desplegar la card.
                orderId={order.id}
                imageIDs={order.imageIDs}
                canAttachPhotos={canAttachPhotos}
                canChangeCause={canChangeSegmentCause}
                // D6: Order padre del RV (misma forma que ActionOT.parentOrder)
                // para la cadena causa→action del ChangePauseCauseModal.
                parentOrder={{
                  id: data[0].id,
                  type: data[0].type,
                  roid: Array.isArray(data[0].roid)
                    ? data[0].roid.join('.')
                    : data[0].roid,
                }}
                revalidatorFunction={revalidatorFunction}
              />
            );
          }
        })}
      </ContainerOT>
    </Container>
  );
};
